import { useState } from "react";
import { Calendar, MapPin, Tag, ArrowLeft, Clock, ChevronRight } from "lucide-react";
import { events } from "../data"; // each item must have: id, title, date, location, excerpt, content, category, tags, image, contentImages (array), type: 'event' | 'blog'
import heroBg from "../assets/bg/bg-hero-4.webp";


const categories = ["All", "Ceremony", "Workshop", "Community", "Health", "Story", "Foundation Story"];

// category colours for badges
const catColors = {
  Ceremony: "badge-gold",
  Workshop: "badge-teal",
  Community: "badge-green",
  Health: "badge-gold",
  Story: "badge-teal",
  "Foundation Story": "badge-green",
};

// Utility: if event.image is missing, fallback to a gradient
const getHeroStyle = (event) => {
  if (event.image) {
    return {
      backgroundImage: `linear-gradient(rgba(17,29,24,0.75), rgba(17,29,24,0.75)), url(${event.image})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    };
  }
  const fallbackGradients = {
    Ceremony: "linear-gradient(135deg,#1B4332,#D4A017)",
    Workshop: "linear-gradient(135deg,#1B4332,#2D6A4F)",
    Community: "linear-gradient(135deg,#2D6A4F,#40916C)",
    Health: "linear-gradient(135deg,#B91C1C,#1B4332)",
    Story: "linear-gradient(135deg,#1B4332,#40916C)",
    "Foundation Story": "linear-gradient(135deg,#0D2B1D,#2D6A4F)",
  };
  return { background: fallbackGradients[event.category] || "linear-gradient(135deg,#1B4332,#2D6A4F)" };
};

// ---------- Event Card ----------
function EventCard({ event, onClick }) {
  return (
    <div className="card" style={{ cursor: "pointer", overflow: "hidden" }} onClick={onClick}>
      <div style={{ height: 200, position: "relative", overflow: "hidden" }}>
        <img
          src={event.image || "https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=400&h=300&fit=crop"}
          alt={event.title}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
       
      </div>
      <div style={{ padding: "1.6rem" }}>
        <div style={{ display: "flex", gap: "1rem", marginBottom: "0.85rem", flexWrap: "wrap" }}>
          <span style={{ display: "flex", alignItems: "center", gap: "0.35rem", fontSize: "0.8rem", color: "var(--gray)", fontFamily: "var(--font-label)" }}>
            <Calendar size={12} color="var(--gold)" /> {event.date}
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: "0.35rem", fontSize: "0.8rem", color: "var(--gray)", fontFamily: "var(--font-label)" }}>
            <MapPin size={12} color="var(--gold)" /> {event.location}
          </span>
        </div>
        <h3 style={{ fontFamily: "var(--font-display)", fontSize: "1.1rem", lineHeight: 1.35, marginBottom: "0.75rem" }}>{event.title}</h3>
        <p style={{ color: "var(--gray)", fontSize: "0.88rem", lineHeight: 1.7, marginBottom: "1.25rem" }}>{event.excerpt}</p>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
         
          <button style={{ display: "flex", alignItems: "center", gap: "0.25rem", color: "var(--forest-mid)", fontWeight: 600, fontSize: "0.85rem", background: "none", border: "none", cursor: "pointer", fontFamily: "var(--font-label)" }}>
            Read More <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Event Detail (with gallery) ----------
function EventDetail({ event, onBack }) {
  const paragraphs = event.content.split("\n\n");
  const images = event.contentImages || [];

  return (
    <div style={{ minHeight: "60vh" }}>
      {/* Hero with image */}
      <div
        style={{
          ...getHeroStyle(event),
          padding: "5rem 0 4rem",
          position: "relative",
          paddingTop: 100,
        }}
      >
        <div className="container" style={{ position: "relative" }}>
          <button
            onClick={onBack}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              background: "rgba(255,255,255,0.12)",
              border: "none",
              borderRadius: 50,
              padding: "0.45rem 1rem",
              color: "#fff",
              fontFamily: "var(--font-label)",
              fontSize: "0.85rem",
              fontWeight: 600,
              cursor: "pointer",
              marginBottom: "1.5rem",
            }}
          >
            <ArrowLeft size={14} /> Back to {event.type === "event" ? "Events" : "Blogs"}
          </button>
          
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(1.5rem,3vw,2.4rem)",
              color: "#fff",
              lineHeight: 1.25,
              maxWidth: 680,
              marginBottom: "1.25rem",
            }}
          >
            {event.title}
          </h1>
          <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
            <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", color: "rgba(255,255,255,0.8)", fontSize: "0.9rem" }}>
              <Calendar size={14} /> {event.date}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", color: "rgba(255,255,255,0.8)", fontSize: "0.9rem" }}>
              <MapPin size={14} /> {event.location}
            </span>
          </div>
        </div>
      </div>



      <div className="ribbon-divider" />

      <section className="section">
        <div className="container">
          <div style={{ maxWidth: 740, margin: "0 auto" }}>
           
            {/* Excerpt */}
            <p
              style={{
                fontSize: "1.15rem",
                color: "var(--forest-mid)",
              
                fontFamily: "var(--font-display)",
                lineHeight: 1.75,
                marginBottom: "2rem",
                borderLeft: "4px solid var(--gold)",
                paddingLeft: "1.25rem",
              }}
            >
              {event.excerpt}
            </p>

            {/* ----- GALLERY (all images, 3 per row) ----- */}
            {images.length > 0 && (
              <div style={{ marginBottom: "2.5rem" }}>
                <h4 style={{ fontFamily: "var(--font-display)", color: "var(--forest)", marginBottom: "1rem" }}>
                  Gallery
                </h4>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "1rem",
                  }}
                >
                  {images.map((url, idx) => (
                    <div
                      key={idx}
                      style={{
                        overflow: "hidden",
                        borderRadius: "12px",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                      }}
                    >
                      <img
                        src={url}
                        alt={`Gallery image ${idx + 1}`}
                        style={{
                          width: "100%",
                          height: "200px",
                          objectFit: "contain",
                          transition: "transform 0.3s ease",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.02)")}
                        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Body paragraphs (without inline images) */}
            {paragraphs.map((para, idx) => (
              <p
                key={idx}
                style={{
                  color: "var(--charcoal)",
                  lineHeight: 1.85,
                  fontSize: "1rem",
                  marginBottom: "1.4rem",
                }}
              >
                {para}
              </p>
            ))}

            {/* Back Button */}
            <div style={{ marginTop: "3rem", borderTop: "1px solid var(--gray-light)", paddingTop: "2rem" }}>
              <button onClick={onBack} className="btn btn-outline" style={{ borderColor: "var(--forest)", color: "var(--forest)" }}>
                <ArrowLeft size={15} /> Back to {event.type === "event" ? "Events" : "Blogs"}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------- Main Events Component (with tabs) ----------
export default function Events() {
  const [activeTab, setActiveTab] = useState("events"); // "events" or "blogs"
  const [selected, setSelected] = useState(null);
  const [cat, setCat] = useState("All");

  // Separate data
  const upcomingEvents = events.filter(e => e.type === "event");
  const blogPosts = events.filter(e => e.type === "blog");

  // Filter by category if needed (applies to both)
  const filterByCategory = (list) => {
    if (cat === "All") return list;
    return list.filter(e => e.category === cat);
  };

  const filteredEvents = filterByCategory(upcomingEvents);
  const filteredBlogs = filterByCategory(blogPosts);

  // Determine which list to show
  const currentList = activeTab === "events" ? filteredEvents : filteredBlogs;

  if (selected) return <EventDetail event={selected} onBack={() => setSelected(null)} />;

  return (
    <div>
      {/* Hero */}
      <div
        className="page-hero"
       style={{
  backgroundImage: `linear-gradient(rgba(17,29,24,0.78), rgba(17,29,24,0.78)), url(${heroBg})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
}}
      >
        <div className="container">
          <h1
            className="display-lg"
            style={{
              marginTop: "0.5rem",
              marginBottom: "0.75rem",
              color: "var(--gold-light)",
            }}
          >
            Events & Blog
          </h1>
          <p style={{ color: "rgba(255,255,255,0.85)" }}>
            Ceremonies, workshops, health camps, community gatherings, and inspiring stories.
          </p>
        </div>
      </div>

      <div className="ribbon-divider" />

      <section className="section">
        <div className="container">

          {/* Grid of cards for current list */}
          <div className="grid-3">
            {currentList.map(item => (
              <EventCard key={item.id} event={item} onClick={() => setSelected(item)} />
            ))}
          </div>

          {currentList.length === 0 && (
            <div style={{ textAlign: "center", padding: "4rem", color: "var(--gray)" }}>
              No {activeTab === "events" ? "events" : "blogs"} found for this category.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}